import { useState } from 'react';

import { Alert, Button, Modal, Table } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { ErrorBoundary } from '@sentry/react';

import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { getApiErrorMessage } from 'utils/api-error';
import {
  downloadReceipt,
  isRefusal,
  migrationRoutes,
  readBlobError,
  parseReceiptPayload,
  readRegisterFailure,
  readRegisterSummary
} from 'utils/PatientMigration';

interface IMigrateDevicesModalProps {
  uid: string;
  onClose: (migrated?: boolean) => void;
}

const MigrateDevicesModal = ({ uid, onClose }: IMigrateDevicesModalProps) => {
  const { formatMessage } = useIntl();
  // The response body IS the receipt file. The bytes are kept verbatim for download and a parsed
  // copy is used only to render: parsing to display is safe, re-serialising to upload is not,
  // because the signature is over the exact bytes of `payload`.
  const [receiptBytes, setReceiptBytes] = useState<Blob | null>(null);
  const [payload, setPayload] = useState<IMigrationReceiptPayload | null>(null);
  const [summary, setSummary] = useState<IMigrationRegisterSummary | null>(null);
  // Nothing registered: refused with a 422 rather than issued as a receipt authorising nothing.
  const [failure, setFailure] = useState<IMigrationRegisterFailure | null>(null);
  const [failed, setFailed] = useState(false);
  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);

  const [{ loading }, register] = useAxios<Blob>(
    {
      method: 'POST',
      url: migrationRoutes.registerDevices(uid),
      responseType: 'blob'
    },
    { manual: true }
  );

  const handleRegister = async () => {
    setFailure(null);
    setFailed(false);
    setRefusalMessage(null);
    try {
      const response = await register();
      if (!response?.data) return;
      setReceiptBytes(response.data);
      setPayload(parseReceiptPayload(JSON.parse(await response.data.text())));
      setSummary(readRegisterSummary(response.headers));
    } catch (caught) {
      // A refusal is an answer and offers no retry: 422 nothing registered, 404 this patient was
      // not imported from a migration, 409 they are already being moved on somewhere else. Only
      // a transport or server failure is worth repeating.
      const refused = await readRegisterFailure(caught);
      if (refused) {
        setFailure(refused);
        return;
      }
      const read = await readBlobError(caught);
      if (isRefusal(read)) {
        setRefusalMessage(
          getApiErrorMessage(read, formatMessage({ id: 'migrationRegisterFailedKey' }))
        );
        return;
      }
      setFailed(true);
    }
  };

  const imeis = payload?.imeis ?? [];
  // Two different facts that used to look alike. Deferred failed this time and a retry could
  // still register it; unmovable never will, and the remedy is to take the device off the
  // patient or abandon the move. Rendering them together would offer the wrong action for one.
  const deferred = failure?.deferred ?? summary?.reasons ?? [];
  const unmovable = failure?.unmovable ?? [];
  const done = !!receiptBytes;

  return (
    <Modal show backdrop="static" size="lg" data-test="migrate-devices-modal">
      <Modal.Header closeButton onHide={() => onClose(done)}>
        <Modal.Title>{formatMessage({ id: 'migrationMigrateDevicesKey' })}</Modal.Title>
      </Modal.Header>
      {/* The footer, and so the download, sits outside this boundary on purpose: whatever fails
          in the body, the operator can still take away the file the source needs. */}
      <Modal.Body>
        <ErrorBoundary
          fallback={() => (
            <Alert variant="warning" className="mb-0" data-test="migrate-devices-body-fallback">
              {formatMessage({ id: 'migrationReceiptBodyFailedKey' })}
            </Alert>
          )}
        >
          {loading && <Loading />}
          {failed && <Error retry={handleRegister} />}

          {!done && !failure && !refusalMessage && !loading && (
            <p data-test="migrate-devices-intro">
              {formatMessage({ id: 'migrationMigrateDevicesIntroKey' })}
            </p>
          )}

          {refusalMessage && (
            <Alert variant="danger" data-test="migrate-devices-refusal-message">
              {refusalMessage}
            </Alert>
          )}

          {failure && (
            <Alert variant="danger" data-test="migrate-devices-refused">
              <div className="fw-bold mb-1">
                {formatMessage({ id: 'migrationNothingRegisteredKey' })}
              </div>
              {failure.message}
            </Alert>
          )}

          {done && (
            <Alert variant="success" data-test="migrate-devices-registered">
              {formatMessage(
                { id: 'migrationDevicesRegisteredKey' },
                { count: summary?.registered ?? imeis.length }
              )}
            </Alert>
          )}

          {imeis.length > 0 && (
            <Table responsive size="sm" data-test="migrate-devices-results">
              <thead>
                <tr>
                  <th>{formatMessage({ id: 'imeiKey' })}</th>
                  <th>{formatMessage({ id: 'status' })}</th>
                </tr>
              </thead>
              <tbody>
                {imeis.map((imei) => (
                  <tr key={imei} data-test={`migrate-device-${imei}`}>
                    <td className="font-monospace">{imei}</td>
                    <td>{formatMessage({ id: 'migrationDeviceRegisteredKey' })}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {unmovable.length > 0 && (
            <Alert variant="danger" data-test="migrate-devices-unmovable">
              <div className="fw-bold mb-1">
                {formatMessage({ id: 'migrationUnmovableDevicesKey' }, { count: unmovable.length })}
              </div>
              <ul className="mb-0">
                {unmovable.map((device, index) => (
                  <li key={device.imei ?? device.deviceId ?? `unmovable-${index}`}>
                    <span className="font-monospace">{device.imei ?? device.deviceId}</span>
                    {device.reason ? `: ${device.reason}` : ''}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {deferred.length > 0 && (
            <Alert variant="warning" data-test="migrate-devices-deferred">
              <div className="fw-bold mb-1">
                {formatMessage({ id: 'migrationDevicesDeferredKey' }, { count: deferred.length })}
              </div>
              <ul className="mb-0">
                {deferred.map((device) => (
                  <li key={device.imei}>
                    <span className="font-monospace">{device.imei}</span>
                    {device.reason ? `: ${device.reason}` : ''}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {done && (
            <Alert variant="info" className="mb-0" data-test="migrate-devices-receipt-hint">
              {formatMessage({ id: 'migrationReceiptHintKey' })}
            </Alert>
          )}
        </ErrorBoundary>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={() => onClose(done)} data-test="migrate-devices-close">
          {formatMessage({ id: done ? 'closeKey' : 'Cancel' })}
        </Button>
        {done ? (
          <Button
            variant="primary"
            /* Named by the SOURCE uid: the file is consumed on the source environment, where the
               destination uid means nothing to whoever has to find it. */
            onClick={() => receiptBytes && downloadReceipt(receiptBytes, payload?.sourceUid ?? uid)}
            data-test="migrate-devices-download"
          >
            {formatMessage({ id: 'migrationDownloadReceiptKey' })}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleRegister}
            disabled={loading || !!failure || !!refusalMessage}
            data-test="migrate-devices-confirm"
          >
            {formatMessage({ id: 'migrationMigrateDevicesKey' })}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MigrateDevicesModal;
